import React, { useEffect, useState } from 'react';
import AdminHeader from '../../components/admin/AdminHeader';
import { useTranslation } from 'react-i18next';
import { Upload, Calendar, X, Eye, CircleSlash } from 'lucide-react';

type OrderRow = {
  id: string;
  short_id?: string;
  public_id?: string;
  status: string;
  customer_name: string;
  customer_email: string;
  total_mxn: number;
  piece_count: number;
  total_paid_mxn?: number;
  delivery_at?: string;
  created_at: string;
  requires_invoice?: boolean;
  has_proof?: boolean;
  has_qr?: boolean;
};

export default function OrdersPage(): React.ReactElement {
  const { t } = useTranslation();
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [loading, setLoading] = useState(false);
  const baseUrl =
    (window as unknown as { API_BASE_URL?: string }).API_BASE_URL || window.location.origin;
  const [orderFilter, setOrderFilter] = useState<string>(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      return sp.get('order') || '';
    } catch {
      return '';
    }
  });
  const [sortBy, setSortBy] = useState<keyof OrderRow | ''>('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [openPickerId, setOpenPickerId] = useState<string | null>(null);
  const [pickerDate, setPickerDate] = useState<string>('');
  const [pickerTime, setPickerTime] = useState<string>('');
  const pickerRef = React.useRef<HTMLDivElement | null>(null);
  const [openProof, setOpenProof] = useState<{ orderId: string; url: string } | null>(null);

  const fetchOrders = React.useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const url = orderFilter
        ? baseUrl + '/public/api/v1/orders/list?order=' + encodeURIComponent(orderFilter)
        : baseUrl + '/public/api/v1/orders/list';
      const res = await fetch(url);
      const j = await res.json();
      if (j.success && j.data && j.data.orders) {
        const normalized: OrderRow[] = j.data.orders.map((o: { [k: string]: unknown }) => {
          const pid =
            (o['public_id'] as string | undefined) ??
            (o['publicId'] as string | undefined) ??
            (o['publicID'] as string | undefined) ??
            (o['publicid'] as string | undefined);
          const hpRaw = (o['has_proof'] as unknown);
          const hp = hpRaw === 1 || hpRaw === '1' || hpRaw === true;
          const hqRaw = (o['has_qr'] as unknown);
          const hq = hqRaw === 1 || hqRaw === '1' || hqRaw === true;
          return { ...(o as OrderRow), public_id: pid, has_proof: hp, has_qr: hq } as OrderRow;
        });
        setOrders(normalized);
      }
    } finally {
      setLoading(false);
    }
  }, [orderFilter, baseUrl]);

  useEffect(() => {
    fetchOrders();
  }, [orderFilter, fetchOrders]);

  const updateStatus = async (orderId: string, status: string): Promise<void> => {
    await fetch(
      baseUrl + '/public/api/v1/orders/status/' + encodeURIComponent(orderId) + '/' + encodeURIComponent(status),
      { method: 'PUT' }
    );
    fetchOrders();
  };

  const uploadInvoice = async (orderId: string, file: File): Promise<void> => {
    const fd = new FormData();
    fd.append('invoice', file);
    await fetch(baseUrl + '/public/api/v1/orders/invoice/' + orderId, {
      method: 'POST',
      body: fd,
    });
    fetchOrders();
  };

  const scheduleDelivery = async (orderId: string, when: string): Promise<void> => {
    const fd = new FormData();
    fd.append('delivery_at', when);
    await fetch(baseUrl + '/public/api/v1/orders/schedule/' + orderId, {
      method: 'POST',
      body: fd,
    });
    fetchOrders();
  };

  const onSort = (field: keyof OrderRow): void => {
    if (sortBy === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
  };

  const openPicker = (o: OrderRow): void => {
    if (o.status !== 'confirmed') return;
    setOpenPickerId(o.id);
    const base = o.delivery_at || '';
    const d = base ? base.substring(0, 10) : '';
    const t = base ? base.substring(11, 16) : '';
    setPickerDate(d);
    setPickerTime(t);
  };

  const applyPicker = async (): Promise<void> => {
    if (!openPickerId) return;
    const ord = orders.find((oo) => oo.id === openPickerId);
    if (!ord || ord.status !== 'confirmed') { setOpenPickerId(null); return; }
    const v = pickerDate && pickerTime ? `${pickerDate}T${pickerTime}` : '';
    if (v) await scheduleDelivery(openPickerId, v);
    setOpenPickerId(null);
  };

  useEffect(() => {
    const onDocClick = (e: MouseEvent): void => {
      if (!openPickerId) return;
      const el = pickerRef.current;
      if (el && !el.contains(e.target as Node)) {
        setOpenPickerId(null);
      }
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        setOpenPickerId(null);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [openPickerId]);

  const buildProofUrl = (o: OrderRow): string => {
    const key = (o.public_id && o.public_id !== '') ? o.public_id : o.id;
    return baseUrl + '/public/api/v1/orders/download/proof/' + encodeURIComponent(key) + '?inline=1';
  };

  const buildQrUrl = (o: OrderRow): string => {
    const key = (o.public_id && o.public_id !== '') ? o.public_id : o.id;
    return baseUrl + '/public/api/v1/orders/download/qr/' + encodeURIComponent(key) + '?inline=1';
  };

  const openProofModal = (o: OrderRow): void => {
    setOpenProof({ orderId: o.id, url: buildProofUrl(o) });
  };

  const uploadQr = async (orderId: string, file: File): Promise<void> => {
    const fd = new FormData();
    fd.append('qr_image', file);
    await fetch(baseUrl + '/public/api/v1/orders/qr/' + orderId, {
      method: 'POST',
      body: fd,
    });
    fetchOrders();
  };

  useEffect(() => {
    const urls = [
      baseUrl + '/public/api/v1/orders/stream',
      baseUrl + '/public/api/v1/index.php/orders/stream',
    ];
    let idx = 0;
    let es: EventSource | null = null;
    let retries = 0;
    let timer: number | null = null;
    const maxRetriesBeforeSwitch = 3;
    const baseDelayMs = 1000;
    const connect = (): void => {
      setSseStatus('connecting');
      try {
        es = new EventSource(urls[idx]);
        es.onopen = () => { setSseStatus('open'); retries = 0; };
        es.onerror = () => {
          setSseStatus('error');
          if (es) { es.close(); es = null; }
          retries += 1;
          const delay = Math.min(10000, baseDelayMs * Math.pow(2, Math.min(6, retries)));
          if (timer) { clearTimeout(timer); timer = null; }
          timer = window.setTimeout(() => {
            if (retries >= maxRetriesBeforeSwitch) { idx = (idx + 1) % urls.length; retries = 0; }
            connect();
          }, delay);
        };
        es.addEventListener('order_created', (e: MessageEvent) => {
          try {
            const r = JSON.parse(e.data) as OrderRow;
            setOrders((prev) => {
              const exists = prev.some((p) => p.id === r.id);
              if (exists) return prev.map((p) => (p.id === r.id ? { ...p, ...r } : p));
              return [r, ...prev];
            });
          } catch {}
        });
        es.addEventListener('order_updated', (e: MessageEvent) => {
          try {
            const ch = JSON.parse(e.data) as Partial<OrderRow> & { id: string };
            setOrders((prev) => prev.map((p) => (p.id === ch.id ? { ...p, ...ch } : p)));
          } catch {}
        });
      } catch {
        setSseStatus('error');
        retries += 1;
        const delay = Math.min(10000, baseDelayMs * Math.pow(2, Math.min(6, retries)));
        if (timer) { clearTimeout(timer); timer = null; }
        timer = window.setTimeout(() => {
          if (retries >= maxRetriesBeforeSwitch) { idx = (idx + 1) % urls.length; retries = 0; }
          connect();
        }, delay);
      }
    };
    connect();
    return () => {
      if (timer) { clearTimeout(timer); timer = null; }
      if (es) es.close();
      setSseStatus('closed');
    };
  }, [baseUrl]);

  const sortedOrders = React.useMemo(() => {
    if (!sortBy) return orders;
    const copy = orders.slice();
    const getVal = (o: OrderRow): string | number => {
      if (sortBy === 'delivery_at') return o.delivery_at || '';
      const v = (o as Record<string, unknown>)[sortBy];
      return typeof v === 'number' ? v : String(v ?? '');
    };
    copy.sort((a, b) => {
      if (sortBy === 'delivery_at') {
        const ta = Date.parse(a.delivery_at || '');
        const tb = Date.parse(b.delivery_at || '');
        return sortDir === 'asc' ? ta - tb : tb - ta;
      }
      const va = getVal(a);
      const vb = getVal(b);
      if (typeof va === 'number' && typeof vb === 'number') {
        return sortDir === 'asc' ? va - vb : vb - va;
      }
      const sa = String(va);
      const sb = String(vb);
      return sortDir === 'asc' ? sa.localeCompare(sb) : sb.localeCompare(sa);
    });
    return copy;
  }, [orders, sortBy, sortDir]);

  const liveOrders = React.useMemo(() => {
    const excluded = new Set(['delivered', 'finished', 'canceled']);
    return orders.filter((o) => !excluded.has(o.status));
  }, [orders]);

  const totals = React.useMemo(() => {
    const totalOrdered = liveOrders.reduce((sum, o) => sum + Number(o.total_mxn || 0), 0);
    const totalReportedPaid = liveOrders.reduce((sum, o) => sum + Number(o.total_paid_mxn || 0), 0);
    const paidOrders = liveOrders.filter((o) => o.status === 'paid');
    const totalPaidConfirmed = paidOrders.reduce((sum, o) => sum + Number(o.total_paid_mxn || 0), 0);
    const piecesPaidConfirmed = paidOrders.reduce((sum, o) => sum + Number(o.piece_count || 0), 0);
    return { totalOrdered, totalReportedPaid, totalPaidConfirmed, piecesPaidConfirmed };
  }, [liveOrders]);
  const fmtCurrency = React.useMemo(() => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2, maximumFractionDigits: 2 }), []);

  const ThSort: React.FC<{ label: string | undefined; field: keyof OrderRow }> = ({
    label,
    field,
  }) => {
    const active = sortBy === field;
    const icon = active ? (sortDir === 'asc' ? '▲' : '▼') : '';
    return (
      <th
        onClick={() => onSort(field)}
        style={{
          cursor: 'pointer',
          userSelect: 'none',
          backgroundColor: active ? '#f3f4f6' : undefined,
          textAlign: 'left',
        }}
      >
        {label}
        {icon ? <span style={{ marginLeft: 6, fontSize: 12 }}>{icon}</span> : null}
      </th>
    );
  };

  const [sseStatus, setSseStatus] = useState<'connecting' | 'open' | 'error' | 'closed'>('connecting');
  return (
    <div>
      <AdminHeader />
      <div style={{ padding: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2>{t('admin.menu.orders')}</h2>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: (sseStatus === 'open' ? '#065f46' : (sseStatus === 'error' ? '#92400e' : '#374151')) }}>
            <span style={{ width: 8, height: 8, borderRadius: 9999, backgroundColor: (sseStatus === 'open' ? '#10b981' : (sseStatus === 'error' ? '#f59e0b' : '#9ca3af')) }}></span>
            <span>{sseStatus === 'open' ? 'En vivo' : (sseStatus === 'error' ? 'Reconectando…' : 'Conectando…')}</span>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              gap: 12,
              backgroundColor: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: 12,
              boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)',
              padding: 12,
              width: 480,
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ fontSize: 11, color: '#6b7280' }}>Total Ordenado</div>
              <div style={{ fontSize: 18, fontWeight: 600 }}>
                {fmtCurrency.format(totals.totalOrdered)}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ fontSize: 11, color: '#6b7280' }}>Reportado como Pagado</div>
              <div style={{ fontSize: 18, fontWeight: 600 }}>
                {fmtCurrency.format(totals.totalReportedPaid)}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ fontSize: 11, color: '#6b7280' }}>Total Confirmado</div>
              <div style={{ fontSize: 18, fontWeight: 600 }}>
                {fmtCurrency.format(totals.totalPaidConfirmed)}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ fontSize: 11, color: '#6b7280' }}>
                {t('admin.columns.pieces') + ' (' + t('admin.statuses.paid') + ')'}
              </div>
              <div style={{ fontSize: 18, fontWeight: 600 }}>
                {totals.piecesPaidConfirmed}
              </div>
            </div>
          </div>
        </div>
        <div style={{ margin: '8px 0' }}>
          <input
            type="text"
            placeholder={t('admin.orders.filter_placeholder')}
            value={orderFilter}
            onChange={(e) => setOrderFilter(e.target.value)}
            style={{
              padding: 8,
              border: '1px solid #ccc',
              borderRadius: 6,
              width: 320,
              marginRight: 8,
            }}
          />
          <button onClick={fetchOrders} style={{ padding: '8px 12px', borderRadius: 6 }}>
            {t('admin.orders.search')}
          </button>
        </div>
        {loading ? (
          <div>{t('common.loading')}</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                <ThSort label={t('admin.columns.id')} field="public_id" />
                <ThSort label={t('admin.columns.status')} field="status" />
                <ThSort label={t('admin.columns.customer')} field="customer_name" />
                <th style={{ textAlign: 'left' }}>{t('admin.columns.total')}</th>
                <th style={{ textAlign: 'left' }}>{t('admin.columns.pieces')}</th>
                <th style={{ textAlign: 'left' }}>{t('admin.columns.payment')}</th>
                <ThSort label={t('admin.columns.delivery_date')} field="delivery_at" />
                <th style={{ textAlign: 'left' }}>{t('admin.columns.invoice')}</th>
                <th style={{ textAlign: 'left' }}>{t('admin.columns.qr')}</th>
              </tr>
            </thead>
            <tbody>
              {sortedOrders.map((o, idx) => (
                <tr
                  key={o.id}
                  style={{
                    borderTop: '1px solid #e5e7eb',
                    backgroundColor: idx % 2 === 0 ? '#f9fafb' : 'white',
                  }}
                >
                  <td>{o.public_id ?? '-'}</td>
                  <td>
                    <select
                      defaultValue={o.status}
                      onChange={(e) => updateStatus(o.id, e.target.value)}
                    >
                      {[
                        'received',
                        'paid',
                        'scheduled',
                        'in_production',
                        'delivered',
                        'warranty',
                        'finished',
                        'canceled',
                        'confirmed',
                      ].map((s) => (
                        <option key={s} value={s}>
                          {t(`admin.statuses.${s}`)}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    {o.customer_name}{' '}
                    <div style={{ fontSize: 11, color: '#555' }}>{o.customer_email}</div>
                  </td>
                  <td>${Number(o.total_mxn).toFixed(2)}</td>
                  <td>{o.piece_count}</td>
                  <td style={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                    <span style={{ display: 'inline-block' }}>
                      {o.total_paid_mxn ? ('$' + Number(o.total_paid_mxn).toFixed(2)) : '-'}
                    </span>
                    {o.has_proof ? (
                      <button
                        type="button"
                        onClick={() => openProofModal(o)}
                        style={{
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          marginLeft: 8,
                        }}
                        aria-label={t('admin.columns.payment')}
                        title={t('admin.columns.payment')}
                      >
                        <Eye size={16} />
                      </button>
                    ) : (
                      <span title={t('admin.columns.payment')} aria-label={t('admin.columns.payment')} style={{ marginLeft: 8 }}>
                        <CircleSlash size={16} color="#9ca3af" />
                      </span>
                    )}
                  </td>
                  <td style={{ position: 'relative' }}>
                    <button
                      type="button"
                      onClick={() => openPicker(o)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '6px 10px',
                        fontSize: 12,
                        border: '1px solid #d1d5db',
                        borderRadius: 9999,
                        backgroundColor: o.status === 'confirmed' ? '#fff' : '#f9fafb',
                        color: o.status === 'confirmed' ? '#111827' : '#9ca3af',
                      }}
                      disabled={o.status !== 'confirmed'}
                      aria-label={t('admin.columns.delivery_date')}
                    >
                      <Calendar size={16} />
                      {o.delivery_at ? o.delivery_at.substring(0, 16).replace('T', ' ') : '—'}
                    </button>
                    {openPickerId === o.id ? (
                      <div
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          marginTop: 8,
                          backgroundColor: '#fff',
                          border: '1px solid #e5e7eb',
                          borderRadius: 12,
                          boxShadow:
                            '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)',
                          padding: 12,
                          zIndex: 50,
                          width: 240,
                        }}
                        ref={pickerRef}
                      >
                        <div style={{ display: 'flex', gap: 8 }}>
                          <input
                            type="date"
                            value={pickerDate}
                            onChange={(e) => setPickerDate(e.target.value)}
                            style={{
                              flex: 1,
                              padding: 6,
                              fontSize: 12,
                              border: '1px solid #d1d5db',
                              borderRadius: 8,
                            }}
                          />
                          <input
                            type="time"
                            value={pickerTime}
                            onChange={(e) => setPickerTime(e.target.value)}
                            style={{
                              width: 90,
                              padding: 6,
                              fontSize: 12,
                              border: '1px solid #d1d5db',
                              borderRadius: 8,
                            }}
                          />
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            marginTop: 10,
                            gap: 8,
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => setOpenPickerId(null)}
                            style={{
                              padding: '6px 10px',
                              fontSize: 12,
                              borderRadius: 8,
                              backgroundColor: '#fff',
                              color: '#111827',
                              border: '1px solid #e5e7eb',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                            }}
                            aria-label={t('admin.columns.delivery_date')}
                          >
                            <X size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={applyPicker}
                            style={{
                              padding: '6px 10px',
                              fontSize: 12,
                              borderRadius: 8,
                              backgroundColor: '#111827',
                              color: '#fff',
                              border: 'none',
                            }}
                          >
                            {t('common.apply')}
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </td>
                  <td>
                    {o.requires_invoice ? (
                      <>
                        <input
                          id={'inv-' + o.id}
                          type="file"
                          accept=".zip"
                          style={{ display: 'none' }}
                          onChange={(e) => e.target.files && uploadInvoice(o.id, e.target.files[0])}
                        />
                        <button
                          type="button"
                          onClick={() => document.getElementById('inv-' + o.id)?.click()}
                          style={{
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            background: 'none',
                            border: 'none',
                            padding: 0,
                          }}
                          aria-label={t('admin.columns.invoice')}
                        >
                          <Upload size={18} />
                        </button>
                      </>
                    ) : (
                      <span style={{ fontSize: 12, color: '#6b7280' }}>
                        {t('admin.orders.invoice.not_requested')}
                      </span>
                    )}
                  </td>
                  <td style={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                    <input
                      id={'qr-' + o.id}
                      type="file"
                      accept="image/png,image/jpeg"
                      style={{ display: 'none' }}
                      onChange={(e) => e.target.files && uploadQr(o.id, e.target.files[0])}
                    />
                    <button
                      type="button"
                      onClick={() => document.getElementById('qr-' + o.id)?.click()}
                      style={{
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        background: 'none',
                        border: '1px solid #e5e7eb',
                        borderRadius: 8,
                        padding: '4px 8px',
                        marginRight: 8,
                      }}
                      aria-label={t('admin.columns.qr')}
                      title={t('admin.columns.qr')}
                    >
                      <Upload size={16} />
                    </button>
                    {o.has_qr ? (
                      <a
                        href={buildQrUrl(o)}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={t('admin.columns.qr')}
                        title={t('admin.columns.qr')}
                        style={{ display: 'inline-flex', alignItems: 'center' }}
                      >
                        <Eye size={16} />
                      </a>
                    ) : (
                      <span title={t('admin.columns.qr')} aria-label={t('admin.columns.qr')} style={{ marginLeft: 0 }}>
                        <CircleSlash size={16} color="#9ca3af" />
                      </span>
                    )}
                  </td>
                  
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {openProof ? (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 100,
            }}
          >
            <div
              style={{
                backgroundColor: '#fff',
                borderRadius: 12,
                boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)',
                width: '90vw',
                maxWidth: 900,
                maxHeight: '85vh',
                overflow: 'auto',
              }}
            >
              <div style={{ padding: 12, display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setOpenProof(null)}
                  style={{
                    padding: '6px 10px',
                    fontSize: 12,
                    borderRadius: 8,
                    backgroundColor: '#fff',
                    color: '#111827',
                    border: '1px solid #e5e7eb',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                  aria-label={t('common.close')}
                >
                  <X size={16} />
                </button>
              </div>
              <div style={{ padding: 12 }}>
                <iframe
                  src={openProof.url}
                  title="payment-proof"
                  style={{ width: '100%', height: '70vh', border: 'none' }}
                />
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
